import { FilterDataPointMetadataValue, FilterDataPointString } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { Ellipsis } from '@/components/Ellipsis';
import { ExpandPopoverButton } from '@/components/Search/FilterPopover';
import { Close } from '@mui/icons-material';
import { Box, Popover, Tooltip } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { CustomLabel } from './CustomLabel';
import { FilterPopoverContent } from './FilterPopoverContent';
import { FilterTree } from './FilterTree';
import { FilterTriggerContent } from './FilterTriggerContent';
import {
  buildFolderTree,
  findItemIdByFilter,
  findNodeByFilter,
  generateExpandedItems,
  getExpandedItemsForSearch,
} from './treeUtils';
import { FolderTreeNode } from './types';

export const FilterDataPopover = ({
  filterData,
  filterName,
  recursive = false,
  onFilterChange,
  searchQuery = '',
  selectedFilterData,
  selectedFilterValues,
  enableMultiSelect = false,
  isDeferredApply,
  showFilterInput = true,
  selectedFilterValue,
  isStale = false,
  onRemove,
  readonly = false,
  hideZeroCounts = false,
  useJsonPayload = false,
  staleFilterValues = [],
}: {
  filterData: (FilterDataPointString | FilterDataPointMetadataValue)[];
  filterName?: string;
  recursive?: boolean;
  onFilterChange?: (checkedCount: number, changedValue: string) => void;
  searchQuery?: string;
  selectedFilterData?: FilterDataPointString | FilterDataPointMetadataValue;
  selectedFilterValues?: string[];
  enableMultiSelect?: boolean;
  isDeferredApply?: boolean;
  showFilterInput?: boolean;
  selectedFilterValue?: React.ReactNode;
  isStale?: boolean;
  onRemove?: () => void;
  readonly?: boolean;
  hideZeroCounts?: boolean;
  useJsonPayload?: boolean;
  staleFilterValues?: string[];
}) => {
  const [selectedItems, setSelectedItems] = useState<string>('tree-0-0');
  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>();
  const [filterInputValue, setFilterInputValue] = useState<string>('');

  const folderTree = useMemo(
    () => buildFolderTree(filterData, filterName, recursive),
    [filterData, filterName, recursive]
  );

  const getAppliedSelection = (): string[] => {
    let selectedDataValue: string | undefined;
    if (selectedFilterData) {
      selectedDataValue =
        typeof selectedFilterData.filter === 'string' ? selectedFilterData.filter : String(selectedFilterData.filter);
    }

    if (enableMultiSelect) {
      if (selectedFilterValues && selectedFilterValues.length > 0) {
        return selectedFilterValues;
      } else if (selectedDataValue) {
        return [selectedDataValue];
      } else {
        return [];
      }
    }

    return selectedDataValue ? [selectedDataValue] : [];
  };

  const [userExpandedItems, setUserExpandedItems] = useState<string[]>(() => generateExpandedItems(folderTree));

  const handleCheckboxChange = (filterValue: string) => {
    setCheckedItems(previous => {
      let newCheckedItems;
      if (enableMultiSelect) {
        newCheckedItems = previous.includes(filterValue)
          ? previous.filter(f => f !== filterValue)
          : [...previous, filterValue];
      } else {
        newCheckedItems = [filterValue];
      }
      if (!isDeferredApply) {
        onFilterChange?.(newCheckedItems.length, filterValue);
      }
      return newCheckedItems;
    });
  };

  const handleRemoveValue = (filterValue: string) => {
    onFilterChange?.(0, filterValue);
  };

  const open = Boolean(anchorElement);
  const hasSelectedItems = open ? checkedItems.length > 0 : getAppliedSelection().length > 0;

  const filteredTree = useMemo(() => {
    const combinedQuery = searchQuery.trim() || filterInputValue.trim();

    if (!combinedQuery) {
      return folderTree;
    }

    const queryLower = combinedQuery.toLowerCase();
    const filterNameLower = filterName?.toLowerCase();
    const filterNameMatches = filterNameLower?.includes(queryLower) ?? false;

    const filterNodes = (nodes: FolderTreeNode[]): FolderTreeNode[] => {
      return nodes.reduce<FolderTreeNode[]>((accumulator, node) => {
        const matchesSearch =
          filterNameMatches || node.filterLabelLower.includes(queryLower) || node.filterPathLower.includes(queryLower);

        const filteredSubFolders = filterNodes(node.subFolders);
        const hasMatchingChildren = filteredSubFolders.length > 0;

        if (matchesSearch || hasMatchingChildren) {
          accumulator.push({
            ...node,
            subFolders: filteredSubFolders,
          });
        }
        return accumulator;
      }, []);
    };

    return filterNodes(folderTree);
  }, [folderTree, searchQuery, filterInputValue, filterName]);

  const combinedQuery = (searchQuery || '').trim() || filterInputValue.trim();
  const expandedItems = useMemo(() => {
    if (combinedQuery) {
      return getExpandedItemsForSearch(filteredTree, searchQuery || '');
    }
    return userExpandedItems;
  }, [combinedQuery, filteredTree, searchQuery, userExpandedItems]);

  const handleToggleTree = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
    if (enableMultiSelect && selectedFilterValues && selectedFilterValues.length > 0) {
      setCheckedItems(selectedFilterValues);
      const first = selectedFilterValues[0];
      const itemId = findItemIdByFilter(folderTree, first);
      if (itemId) setSelectedItems(itemId);
    } else if (selectedFilterData) {
      const value =
        typeof selectedFilterData.filter === 'string' ? selectedFilterData.filter : String(selectedFilterData.filter);
      const itemId = findItemIdByFilter(folderTree, value);
      if (itemId) setSelectedItems(itemId);
      setCheckedItems([value]);
    } else {
      setCheckedItems([]);
      setSelectedItems('tree-0-0');
    }
  };

  const handleClose = () => {
    setAnchorElement(undefined);
    setFilterInputValue('');
  };

  const handleApply = () => {
    const pending = new Set(checkedItems);

    if (enableMultiSelect) {
      if (useJsonPayload) {
        const payload = JSON.stringify([...pending]);
        onFilterChange?.(pending.size, payload);
      } else {
        const applied = getAppliedSelection();
        const first = [...pending][0];
        if (first) {
          onFilterChange?.(1, first);
        } else if (applied[0]) {
          onFilterChange?.(0, applied[0]);
        }
      }
    } else {
      const applied = getAppliedSelection();
      const first = [...pending][0];
      if (first) {
        onFilterChange?.(1, first);
      } else if (applied[0]) {
        onFilterChange?.(0, applied[0]);
      }
    }

    handleClose();
  };

  const renderLabel = ({
    itemId,
    label,
    _tooltipText,
    count,
    filterValue,
  }: {
    itemId: string;
    label: string;
    _tooltipText: string;
    count?: number;
    filterValue: string;
  }) => (
    <CustomLabel
      itemId={itemId}
      label={label}
      _tooltipText={_tooltipText}
      count={hideZeroCounts && count === 0 ? undefined : count}
      filterValue={filterValue}
      enableMultiSelect={enableMultiSelect}
      checked={checkedItems.includes(filterValue)}
      onToggle={handleCheckboxChange}
    />
  );

  const getDisplayText = () => {
    if (selectedFilterValue) {
      return String(selectedFilterValue);
    }

    const applied = getAppliedSelection();
    const visibleItems = isDeferredApply && open ? checkedItems : applied;

    if (visibleItems.length === 1) {
      const node = findNodeByFilter(folderTree, visibleItems[0]);
      return node ? node.filterLabel : visibleItems[0];
    }

    if (enableMultiSelect && visibleItems.length > 1) {
      return '';
    }

    return '';
  };

  const renderWithFilterName = () => (
    <div data-testid={`filter-popover-${filterName}`}>
      <Tooltip title={isStale ? 'The current search results do not contain this metadata.' : undefined}>
        <Box sx={isStale ? { opacity: 0.6, filter: 'grayscale(0.3)' } : undefined}>
          <ExpandPopoverButton
            onClick={readonly || isStale ? undefined : handleToggleTree}
            showArrow={!readonly && !isStale}
            removeButton={
              isStale && onRemove ? (
                <Tooltip title='Remove filter'>
                  <Box
                    component='div'
                    onClick={event => {
                      event.stopPropagation();
                      onRemove();
                    }}
                    sx={{
                      color: 'text.secondary',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      padding: '2px',
                      backgroundColor: 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '18px',
                      minHeight: '18px',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                    data-testid={`filter-remove-${filterName}`}
                  >
                    <Close sx={{ fontSize: '12px' }} />
                  </Box>
                </Tooltip>
              ) : undefined
            }
          >
            {hasSelectedItems ? (
              <Box data-testid='filter-popover-value'>
                <FilterTriggerContent
                  filterName={filterName}
                  open={open}
                  checkedItems={checkedItems}
                  getAppliedSelection={getAppliedSelection}
                  findLabel={filterValue => {
                    const node = findNodeByFilter(folderTree, filterValue);
                    if (node) return node.filterLabel;

                    // For stale filters not in tree, extract last part of path
                    if (recursive || filterName === 'Folder') {
                      const pathParts = filterValue.split('/').filter(part => part.length > 0);
                      return pathParts.at(-1) || filterValue;
                    }

                    return filterValue;
                  }}
                  onRemoveValue={handleRemoveValue}
                  staleFilterValues={staleFilterValues}
                />
              </Box>
            ) : (
              <Box
                display='flex'
                alignItems='center'
                flexDirection='row'
                style={{ maxWidth: '177px' }}
                gap='4px'
                data-testid='filter-popover-value'
              >
                <BodyText
                  flexShrink={0}
                  component='span'
                  color='text.secondary'
                  sx={{ textWrap: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  data-testid='body-text'
                >
                  {filterName}
                </BodyText>
                <Box component='span' sx={{ color: 'text.secondary', fontWeight: '500', flexShrink: 0, minWidth: 0 }}>
                  <Ellipsis text={getDisplayText()} />
                </Box>
              </Box>
            )}
          </ExpandPopoverButton>
        </Box>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorElement}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            borderRadius: '6px',
            minWidth: '242px',
            marginLeft: '-2px',
            marginTop: '3px',
          },
        }}
      >
        <div data-testid='filter-popover-content'>
          <FilterPopoverContent
            showFilterInput={showFilterInput}
            filterName={filterName}
            filteredTree={filteredTree}
            filterInputValue={filterInputValue}
            setFilterInputValue={value => setFilterInputValue(value)}
            selectedItems={selectedItems}
            setSelectedItems={value => setSelectedItems(value)}
            expandedItems={expandedItems}
            setExpandedItems={value => setUserExpandedItems(value)}
            renderLabel={renderLabel}
            isDeferredApply={isDeferredApply}
            onApply={handleApply}
          />
        </div>
      </Popover>
    </div>
  );

  return (
    <>
      {filterName
        ? renderWithFilterName()
        : filteredTree.length > 0 && (
            <FilterTree
              nodes={filteredTree}
              selectedItems={selectedItems}
              onSelectedItemsChange={setSelectedItems}
              expandedItems={expandedItems}
              onExpandedItemsChange={setUserExpandedItems}
              renderLabel={renderLabel}
              sx={{
                overflowY: 'auto',
                '& .MuiBox-root': { gap: 0 },
                '& .MuiCheckbox-root, & .MuiRadio-root': { padding: '4px' },
                '& .MuiTreeItem-label': {
                  fontSize: '14px',
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%',
                  display: 'block',
                },
                '& .MuiTreeItem-iconContainer': { color: 'text.primary' },
                '& .MuiTreeItem-content': {
                  gap: '0',
                  paddingTop: '0',
                  paddingBottom: '0',
                  border: 'none',
                  '&.Mui-selected': {
                    backgroundColor: 'transparent !important',
                  },
                  '&:hover': {
                    backgroundColor: 'transparent !important',
                  },
                },
                '& .Mui-selected': {
                  backgroundColor: 'transparent !important',
                  '&:hover': {
                    backgroundColor: 'transparent !important',
                  },
                },
              }}
            />
          )}
    </>
  );
};
